import { useEffect } from 'react'
import {
  ChatMessage,
  ChatGeneratedImageContent,
  ChatGeneratedVideoContent,
  ChatRatingContent,
} from 'api/clients/ChatClient'
import { AIJobStatus } from 'types'
import {
  useOptionalChatComparison,
  ChatComparisonPayload,
} from 'screens/Calendar/FullScreenPreview/ChatComparisonContext'

/**
 * Scans backwards through all messages collecting `generated_image` and
 * `generated_video` payloads from successful assistant messages. Stops at
 * any `comparison_resolved` marker (created when the user accepts or rejects).
 *
 * No-ops when called outside a ChatComparisonProvider (e.g. in the Designer).
 */
export const useSyncChatComparison = ({ messages, threadId }: { messages: ChatMessage[]; threadId?: number }) => {
  const comparisonContext = useOptionalChatComparison()
  const setComparisons = comparisonContext?.setComparisons

  useEffect(() => {
    if (!setComparisons) return
    if (threadId === undefined) return

    const unapproved: ChatComparisonPayload[] = []

    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i]

      if (msg.content.some((c) => c.type === 'comparison_resolved')) break
      if (msg.role !== 'assistant') continue
      if (msg.status !== AIJobStatus.Successful) continue

      const generatedImage = msg.content.find((c): c is ChatGeneratedImageContent => c.type === 'generated_image')
      const generatedVideo = msg.content.find((c): c is ChatGeneratedVideoContent => c.type === 'generated_video')

      if (!generatedImage && !generatedVideo) continue

      const ratingContent = msg.content.find((c): c is ChatRatingContent => c.type === 'rated')
      const legacyRating = typeof ratingContent?.rating === 'string' ? ratingContent.rating : null

      if (generatedImage) {
        unapproved.push({
          previewUrl:
            generatedImage.image_url ||
            ((generatedImage.generation_data?.slides as Array<{ image_url?: string }>)?.[0]?.image_url ?? ''),
          designId: generatedImage.design_id,
          mediaFileId: generatedImage.media_file_id ?? '',
          mediaType: 'image',
          slideIndex: generatedImage.slide_index,
          messageId: msg.id,
          threadId,
          rating: legacyRating,
          generationData: generatedImage.generation_data,
          action: generatedImage.action,
          addedSlideIndex: generatedImage.added_slide_index,
        })
      } else if (generatedVideo) {
        unapproved.push({
          previewUrl: generatedVideo.thumbnail_url,
          videoUrl: generatedVideo.video_url,
          designId: generatedVideo.design_id,
          mediaFileId: generatedVideo.media_file_id,
          mediaType: 'video',
          messageId: msg.id,
          threadId,
          rating: legacyRating,
          generationData: generatedVideo.generation_data,
        })
      }
    }

    setComparisons(unapproved)
  }, [setComparisons, messages, threadId])
}
